import { useContext, useEffect, useMemo } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { useSignal, useSignalEffect } from '@preact/signals-react';
import {
  EditorDeclarativeBlock,
  PlacementKind,
} from '@/webapi/use-experience-api';
import { QBItemSelection } from '@/components/query-builder/models';
import { EditorContext } from '@/features/editor/context/editor-context';
import { PageRedirectChange, StickinessMode } from '@/pkg/sdk';
import { DeviceType } from '@/utils/definitions';
import { PageRedirectOptions } from '@/webapi/use-widget-catalog-api';

export function usePageRedirect(originalChange: EditorDeclarativeBlock) {
  useSignals();
  const {
    experienceControls,
    inspectorNav,
    devicePreview,
    deviceNavigation,
    experienceState: { currentVariantIdx, upsertEditorChange },
  } = useContext(EditorContext);

  const initialVariantId = useMemo(() => currentVariantIdx, []);

  const pageRedirectNewChange = useSignal(originalChange);
  const pageRedirectProps = useSignal(
    originalChange?.pageRedirectOpts ||
      ({
        stickinessMode: `none`,
        retainQueryParams: true,
      } as PageRedirectOptions),
  );
  const isSourceModalVisible = useSignal(false);

  const setSourceRules = async (rules: Array<QBItemSelection>) => {
    pageRedirectProps.value = {
      ...pageRedirectProps.value,
      sourceUrlProps: [...rules],
    };

    if (rules?.length > 0) {
      const foundUrl = rules
        .map((rule) => rule.values?.find((val) => isValidUrl(val.value)))
        ?.find((val) => !!val);
      if (foundUrl?.value && isValidUrl(foundUrl.value)) {
        deviceNavigation?.navigateTo(cleanUrl(foundUrl?.value));
      }
    }
  };

  const setTargetUrl = (url: string) => {
    pageRedirectProps.value = {
      ...pageRedirectProps.value,
      targetUrl: cleanUrl(url),
    };
  };

  const setRetainQueryParams = (retainQueryParams: boolean) => {
    pageRedirectProps.value = { ...pageRedirectProps.value, retainQueryParams };
  };

  const setStickinessMode = (stickinessMode: StickinessMode) => {
    pageRedirectProps.value = { ...pageRedirectProps.value, stickinessMode };
  };

  const onNext = () => {
    inspectorNav.gotoTargeting({
      disablePlacementPicker: true,
      disableThemesPicker: true,
    });
  };

  const targetUrlInitialValue = (_1: string, _2: DeviceType) =>
    pageRedirectProps.value?.targetUrl || ``;

  useEffect(() => {
    if (initialVariantId !== currentVariantIdx) {
      // Reload the change log to get the new variant change
      inspectorNav.gotoChangelog();
    }
  }, [currentVariantIdx, initialVariantId]);

  useSignalEffect(() => {
    pageRedirectNewChange.value = {
      ...pageRedirectNewChange.peek(),
      pageRedirectOpts: pageRedirectProps.value,
      block: {
        ...pageRedirectNewChange.peek().block,
        value: {
          destUrl: pageRedirectProps.value.targetUrl,
          redirectAfter: 1,
          retainQueryParams: pageRedirectProps.value.retainQueryParams,
          stickinessMode: pageRedirectProps.value.stickinessMode,
        } as PageRedirectChange,
      },
    };

    if (pageRedirectProps.peek()?.sourceUrlProps) {
      const newTargeting = {
        kind: PlacementKind.OTHER,
        other: [...pageRedirectProps.peek().sourceUrlProps],
      };
      upsertEditorChange(
        pageRedirectNewChange.peek(),
        false,
        false,
        0,
        (draft) => {
          draft.targeting.placement = newTargeting;
        },
      );
    }
  });

  useEffect(() => devicePreview.disableNavigator(), []);

  return {
    pageRedirectProps,
    pageRedirectNewChange,
    initialVariantId,
    isSourceModalVisible: isSourceModalVisible.value,
    targetUrlInitialValue,
    setIsSourceModalVisible: (val: boolean) => {
      isSourceModalVisible.value = val;
    },
    onNext,
    setSourceRules,
    setTargetUrl,
    setRetainQueryParams,
    setStickinessMode,
    experienceControls,
  };
}

function cleanUrl(url: string) {
  return url.trim();
}

function isValidUrl(urlString) {
  const urlPattern = new RegExp(
    `^(https?:\\/\\/)?` + // validate protocol
      `((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|` + // validate domain name
      `((\\d{1,3}\\.){3}\\d{1,3}))` + // validate OR ip (v4) address
      `(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*` + // validate port and path
      `(\\?[;&a-z\\d%_.~+=-]*)?` + // validate query string
      `(\\#[-a-z\\d_]*)?$`,
    `i`,
  ); // validate fragment locator
  return !!urlPattern.test(urlString);
}
